import React from 'react';
import { AlertTriangle, CalendarClock, CalendarOff, HelpCircle } from 'lucide-react';
import type { Ampel } from './types';
import { gewerkeMitLuecke } from './koordinierungLogic';
import type {
    AusgeschlossenesGewerk, GewerkAusPlan, MatrixDaten, MatrixZelle, PaarStatus,
} from './koordinierungLogic';

/**
 * Die Kreuzmatrix — reine Anzeige.
 *
 * Keine Rechnung, keine Datenbank: alles kommt fertig aus `baueMatrix`
 * (`koordinierungLogic.ts`). Das Projekt hat kein jsdom, Komponenten sind
 * nicht testbar — was hier stuende, waere ungeprueft.
 *
 * ─── Warum Nummern in der Kopfzeile ───────────────────────────────────────
 * Eine n-mal-n-Matrix mit ausgeschriebenen Gewerksnamen in der Kopfzeile
 * braucht entweder gedrehte Schrift oder Spalten von 4 cm Breite. Beides wird
 * ab acht Gewerken unlesbar. Die Spalten tragen deshalb die laufende Nummer,
 * die Zeilen den vollen Namen mit derselben Nummer davor — man liest die Zeile
 * und sucht die Nummer, statt den Kopf zu drehen.
 */

interface Props {
    matrix: MatrixDaten;
    ohneZeitraum: AusgeschlossenesGewerk[];
    /** Gewerke, die der Katalog nicht kennt — sie stehen in der Matrix, aber
     *  ohne Regelvorschlag. Hier nur als Hinweis. */
    offeneGewerke: GewerkAusPlan[];
    /** Das gerade geoeffnete Paar, damit die Zelle sichtbar bleibt. */
    aktiv?: string | null;
    onZelle: (schluessel: string) => void;
}

const AMPEL_KLASSE: Record<Ampel, string> = {
    rot: 'bg-rose-500 text-white',
    orange: 'bg-amber-300 text-amber-950',
    grau: 'bg-slate-50 text-slate-300',
};

const STATUS_TITEL: Record<PaarStatus, string> = {
    unbewertet: 'noch nicht bewertet',
    bewertet: 'bewertet',
    geaendert: 'Zeitraum hat sich verschoben',
    veraltet: 'überholt',
};

/** Der Rahmen sagt, wie es um die BEWERTUNG steht; die Fuellung sagt, wie
 *  lange die Ueberschneidung dauert. Zwei Aussagen, zwei Mittel — sonst
 *  konkurrieren Farbe und Bedeutung um dieselbe Flaeche. */
const RAHMEN: Record<PaarStatus, string> = {
    unbewertet: 'ring-2 ring-inset ring-slate-900/40',
    bewertet: '',
    geaendert: 'ring-2 ring-inset ring-blue-600',
    veraltet: 'ring-2 ring-inset ring-slate-400 opacity-60',
};

/** „n Arbeitsschritt(e) ohne Datum" der beteiligten Gewerke — oder leer. */
const lueckenSatz = (a: GewerkAusPlan, b: GewerkAusPlan): string => {
    const teile = [a, b]
        .filter((g, i, alle) => g.schritteOhneDatum > 0 && alle.indexOf(g) === i)
        .map(g => `${g.label}: ${g.schritteOhneDatum} Arbeitsschritt(e) ohne Datum`);
    return teile.length > 0 ? ` — ACHTUNG: ${teile.join('; ')}. Diese Schritte sind nicht mitgerechnet.` : '';
};

const zellenTitel = (a: GewerkAusPlan, b: GewerkAusPlan, z: MatrixZelle): string => {
    if (z.diagonale) return `${a.label} — ein Gewerk gefährdet sich nicht selbst`;
    // Genau hier entsteht der gefährlichste Irrtum des Moduls: eine graue Zelle
    // liest sich als „geprüft, keine gemeinsame Zeit". Hat eines der beiden
    // Gewerke einen undatierten Arbeitsschritt, gilt das aber nur für die
    // datierten — der Rest wurde nie geprüft.
    if (!z.status) return `${a.label} × ${b.label}: keine gemeinsame Zeit${lueckenSatz(a, b)}`;
    return `${a.label} × ${b.label}: ${z.dauerTage} Tag(e) gemeinsam · ${STATUS_TITEL[z.status]}`
        + (z.bestaetigt > 0 ? ` · ${z.bestaetigt} bestätigt` : '')
        + lueckenSatz(a, b);
};

export const MatrixAnsicht: React.FC<Props> = ({
    matrix, ohneZeitraum, offeneGewerke, aktiv, onZelle,
}) => {
    const { gewerke, zellen } = matrix;
    // Gewerke, die in der Matrix STEHEN, deren Zeitraum aber unvollständig ist.
    // Sie fehlen in `ohneZeitraum` — ihre Hülle ist ja gültig.
    const mitLuecke = gewerkeMitLuecke(gewerke);

    return (
        <div className="space-y-4">
            {gewerke.length < 2 ? (
                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                    <CalendarOff className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">
                        Für eine Matrix braucht es mindestens zwei Gewerke mit vollständigem Zeitraum.
                        {gewerke.length === 1 && ' Bisher hat nur eines einen verwertbaren Zeitraum.'}
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-200 p-4">
                    {/* Die Matrix rollt in ihrem eigenen Kasten. Ohne
                        `overflow-x-auto` schiebt sie ab etwa zwölf Gewerken
                        die ganze Seite nach rechts. */}
                    <div className="overflow-x-auto">
                        <table className="border-separate border-spacing-0.5">
                            <thead>
                                <tr>
                                    <th className="sticky left-0 z-10 bg-white text-left text-[11px] font-black text-slate-400 uppercase tracking-wide px-2 py-1">
                                        Gewerk
                                    </th>
                                    {gewerke.map((g, i) => (
                                        <th
                                            key={g.norm}
                                            title={g.label}
                                            className="w-9 text-[11px] font-black text-slate-400 px-0 py-1"
                                        >
                                            {i + 1}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {gewerke.map((a, i) => (
                                    <tr key={a.norm}>
                                        <th
                                            scope="row"
                                            className="sticky left-0 z-10 bg-white text-left text-xs font-bold text-slate-700 whitespace-nowrap pr-3 py-0.5"
                                        >
                                            <span className="text-slate-400 font-black mr-1.5">{i + 1}</span>
                                            {a.label}
                                            {!a.zugeordnet && (
                                                <span
                                                    title="Der Katalog kennt dieses Gewerk nicht — es gibt keine Regelvorschläge."
                                                    className="ml-1.5 inline-flex items-center align-middle text-amber-500"
                                                >
                                                    <HelpCircle className="w-3.5 h-3.5" />
                                                </span>
                                            )}
                                            {/* Das Merkmal gehört AN das Gewerk: die ganze Zeile
                                                zeigt sonst graue Zellen, und grau liest sich als
                                                „geprüft, keine gemeinsame Zeit". */}
                                            {a.schritteOhneDatum > 0 && (
                                                <span
                                                    title={`${a.schritteOhneDatum} Arbeitsschritt(e) ohne verwertbaren Zeitraum — sie stecken NICHT im gerechneten Zeitraum dieser Zeile.`}
                                                    className="ml-1.5 inline-flex items-center align-middle text-amber-600 font-black"
                                                >
                                                    <CalendarClock className="w-3.5 h-3.5" />
                                                    <span className="text-[10px] ml-0.5">{a.schritteOhneDatum}</span>
                                                </span>
                                            )}
                                        </th>
                                        {gewerke.map((b, j) => {
                                            const z = zellen[i][j];
                                            if (z.diagonale) {
                                                return (
                                                    <td key={b.norm} className="p-0">
                                                        <div
                                                            title={zellenTitel(a, b, z)}
                                                            className="w-9 h-8 flex items-center justify-center text-slate-300 bg-slate-100 rounded"
                                                        >
                                                            —
                                                        </div>
                                                    </td>
                                                );
                                            }
                                            const anklickbar = !!z.schluessel;
                                            return (
                                                <td key={b.norm} className="p-0">
                                                    <button
                                                        type="button"
                                                        disabled={!anklickbar}
                                                        onClick={() => z.schluessel && onZelle(z.schluessel)}
                                                        title={zellenTitel(a, b, z)}
                                                        className={`w-9 h-8 rounded text-[11px] font-black flex items-center justify-center transition-shadow
                                                            ${AMPEL_KLASSE[z.ampel]}
                                                            ${z.status ? RAHMEN[z.status] : ''}
                                                            ${aktiv && aktiv === z.schluessel ? 'outline outline-2 outline-offset-1 outline-slate-900' : ''}
                                                            ${anklickbar ? 'cursor-pointer hover:brightness-95' : 'cursor-default'}`}
                                                    >
                                                        {z.dauerTage > 0 ? z.dauerTage : ''}
                                                    </button>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ─── Legende ─────────────────────────────────────── */}
                    <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded bg-rose-500 inline-block" /> über 28 Tage gemeinsam
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded bg-amber-300 inline-block" /> 1 bis 28 Tage
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded bg-slate-50 border border-slate-200 inline-block" /> keine gemeinsame Zeit
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded bg-amber-300 ring-2 ring-inset ring-slate-900/40 inline-block" /> noch nicht bewertet
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="w-4 h-4 rounded bg-amber-300 ring-2 ring-inset ring-blue-600 inline-block" /> Zeitraum verschoben
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <CalendarClock className="w-4 h-4 text-amber-600" /> Arbeitsschritte ohne Datum —
                            nicht mitgerechnet
                        </span>
                        <span>Die Zahl ist die Zahl gemeinsamer Kalendertage.</span>
                    </div>
                </div>
            )}

            {/* ─── Arbeitsschritte ohne Datum ──────────────────────────── */}
            {/* Diese Gewerke STEHEN in der Matrix — anders als die Liste
                darunter. Gerechnet wurde aber nur die Hülle ihrer datierten
                Schritte; ein Schritt „ab 20.09., Ende offen" zieht sie nicht auf
                und fällt in keine Ausschussliste. Ohne diesen Kasten erschiene
                eine echte Überschneidung als geprüft und unbedenklich. */}
            {mitLuecke.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                    <div className="flex items-start gap-2 mb-3">
                        <CalendarClock className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-sm font-bold text-amber-900">
                            {mitLuecke.length} Gewerk(e) in dieser Matrix sind nur teilweise terminiert
                        </p>
                    </div>
                    <ul className="space-y-1.5">
                        {mitLuecke.map(g => (
                            <li key={g.norm} className="text-xs text-amber-900">
                                <span className="font-bold">{g.label}</span>
                                {` — ${g.schritteOhneDatum} von ${g.anzahlSchritte} Arbeitsschritt(en) ohne Datum. `}
                                Gerechnet wird nur {g.von} bis {g.bis}.
                            </li>
                        ))}
                    </ul>
                    <p className="text-[11px] text-amber-800/80 mt-3">
                        Eine leere Zelle in diesen Zeilen heißt „keine gemeinsame Zeit" nur für die
                        datierten Arbeitsschritte — für die übrigen ist die Prüfung nicht gelaufen.
                    </p>
                </div>
            )}

            {/* ─── Gewerke ohne Zeitraum ───────────────────────────────── */}
            {/* Diese Liste ist kein Anhang, sondern Teil des Ergebnisses: eine
                Matrix ohne sie sieht vollständig aus, obwohl ausgerechnet das
                ungeplante Gewerk fehlt — und ein SiGe-Plan, der etwas nicht
                zeigt, wird als „geprüft und ohne Befund" gelesen. */}
            {ohneZeitraum.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                    <div className="flex items-start gap-2 mb-3">
                        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <p className="text-sm font-bold text-amber-900">
                            {ohneZeitraum.length} Gewerk(e) nehmen an der Überschneidungsprüfung nicht teil
                        </p>
                    </div>
                    <ul className="space-y-1.5">
                        {ohneZeitraum.map(a => (
                            <li key={a.gewerk.norm} className="text-xs text-amber-900">
                                <span className="font-bold">{a.gewerk.label}</span>
                                {' — '}
                                {a.hinweis}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {offeneGewerke.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-4 text-xs text-slate-600">
                    <span className="font-bold">Ohne Zuordnung: </span>
                    {offeneGewerke.map(g => g.label).join(', ')}.
                    {' '}Diese Gewerke stehen in der Matrix, bekommen aber keine Regelvorschläge aus dem
                    Katalog. Ein nicht zugeordnetes Gewerk ist kein Nachweis der Gefährdungsfreiheit —
                    bis zur Klärung gilt jede Überschneidung mit ihm als ungeprüft.
                </div>
            )}
        </div>
    );
};
